import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Link } from "react-router-dom";

const ProviderDashboard = () => {
  const [provider, setProvider] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
  try {
    setLoading(true);

    // Get logged in provider
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError) throw userError;
    if (!user) throw new Error("Provider not authenticated");

    // Get provider record
    const {
      data: providerData,
      error: providerError,
    } = await supabase
      .from("providers")
      .select("*")
      .eq("user_id", user.id)
      .eq("is_active", true)
      .single();

    if (providerError) throw providerError;
    if (!providerData)
      throw new Error("Provider record not found");

    setProvider(providerData);

    // Get provider requests
    const {
      data: requestsData,
      error: requestsError,
    } = await supabase
      .from("service_requests")
      .select("*")
      .eq("provider_id", providerData.id)
      .order("created_at", { ascending: false });

    if (requestsError) throw requestsError;

    if (!requestsData?.length) {
      setRequests([]);
      return;
    }

    const requestIds = requestsData.map((r) => r.id);

    // Fetch all related data
    const [
      { data: contacts, error: contactsError },
      { data: messages, error: messagesError },
      { data: images, error: imagesError },
      { data: progress, error: progressError },
    ] = await Promise.all([
      supabase
        .from("request_contact_info")
        .select("*")
        .in("request_id", requestIds),

      supabase
        .from("request_messages")
        .select("*")
        .in("request_id", requestIds),

      supabase
        .from("request_images")
        .select("*")
        .in("request_id", requestIds),

      supabase
        .from("request_progress")
        .select("*")
        .in("request_id", requestIds),
    ]);

    if (
      contactsError ||
      messagesError ||
      imagesError ||
      progressError
    ) {
      throw (
        contactsError ||
        messagesError ||
        imagesError ||
        progressError
      );
    }

    // Merge everything
    const mergedRequests = requestsData.map((request) => ({
      ...request,

      contact:
        contacts?.find(
          (c) => c.request_id === request.id
        ) || null,

      request_messages:
        messages?.filter(
          (m) => m.request_id === request.id
        ) || [],

      request_images:
        images?.filter(
          (i) => i.request_id === request.id
        ) || [],

      request_progress:
        progress?.filter(
          (p) => p.request_id === request.id
        ) || [],
    }));

    setRequests(mergedRequests);

    console.log("Provider:", providerData);
    console.log("Requests:", mergedRequests);

  } catch (error) {
    console.error("Provider Dashboard Error:", error);
    setRequests([]);
  } finally {
    setLoading(false);
  }
};

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/provider/login";
  };

  // Helper stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => ["pending", "assigned", "in-progress"].includes(r.status?.toLowerCase())).length,
    completed: requests.filter(r => r.status?.toLowerCase() === "completed").length,
    messages: requests.reduce((acc, r) => acc + (r.request_messages?.length || 0), 0),
    images: requests.reduce((acc, r) => acc + (r.request_images?.length || 0), 0),
  };

  if (loading) return <div className="flex h-screen items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Provider Dashboard</h1>
            <p className="text-slate-600">{provider?.full_name} • <span className="text-indigo-600 font-medium">{provider?.service_type}</span></p>
          </div>
          <button onClick={logout} className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition">
            Logout
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: "Total Tasks", val: stats.total, color: "text-slate-800" },
            { label: "Active", val: stats.pending, color: "text-orange-600" },
            { label: "Completed", val: stats.completed, color: "text-green-600" },
            { label: "Messages", val: stats.messages, color: "text-blue-600" },
            { label: "Images", val: stats.images, color: "text-purple-600" },
          ].map((stat, i) => (
            <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{stat.label}</p>
              <h3 className={`text-2xl font-bold ${stat.color}`}>{stat.val}</h3>
            </div>
          ))}
        </div>

        {/* Request List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-5 border-b bg-slate-50">
            <h2 className="font-bold text-slate-700">My Service Requests</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {requests.length > 0 ? (
              requests.map((req) => (
                <div key={req.id} className="p-6 hover:bg-slate-50 transition flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                        req.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {req.status || 'New'}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">{req.id.slice(0,8)}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">{req.service_type}</h3>
                    
                    {/* Client Info from request_contact_info */}
                    <p className="text-sm text-slate-600">
                      Client: <span className="font-medium">{req.contact?.name || req.profiles?.full_name || "N/A"}</span>
                    </p>
                    
                    <div className="flex flex-wrap gap-4 mt-2">
                      <div className="flex items-center text-xs text-slate-500">
                         💬 {req.request_messages?.length || 0} messages
                      </div>
                      <div className="flex items-center text-xs text-slate-500">
                         📸 {req.request_images?.length || 0} images
                      </div>
                      <div className="flex items-center text-xs text-slate-500">
                         📍 {req.request_progress?.length || 0} updates
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/request/${req.id}`}
                    className="w-full md:w-auto text-center bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium transition shadow-sm"
                  >
                    View Details
                  </Link>
                </div>
              ))
            ) : (
              <div className="p-20 text-center text-slate-400">
                No requests assigned to you yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderDashboard;